/***
 * Summary: here resides the scheme and roles for the fields of the network performing
 * 
 */
import { yupToFormErrors } from "formik";
import * as Yup from "yup";

const createNumberValidation = (fieldName) =>
    Yup.number()
        .typeError(`Invalid data. '${fieldName}' must be a number`)
        .nullable(true)
        .test('is-number', `Invalid data. '${fieldName}' must be a number`, function (value) {
            return (value === null || value === undefined || !isNaN(value)) || this.createError({ message: `Invalid data. '${fieldName}' must be a number` });
        });

const TODIS_LAIRPORT_SCHEMA = Yup.object().shape({
    //general section Rules:
    nameResp: Yup.string("Road ID is alphanumeric").nullable(true),
    addressResp: Yup.string("Road ID is alphanumeric").nullable(true),
    contactPer: Yup.string("Road ID is alphanumeric").nullable(true),
    contactPos: Yup.string("Road ID is alphanumeric").nullable(true),
    conctactNum: Yup.string()
        .transform((originalValue) => {
            // Si el valor comienza con '+', eliminamos ese símbolo
            return typeof originalValue == 'string' ? originalValue.replace(/^\+/, '') : originalValue;
        })
        .nullable(true)
        .test('is-valid', 'Invalid data. \'Phone Number\' must be a valid number', function (value) {
            // Permitir números, espacios, y la presencia de '+'
            const isValid = /^[0-9+\s]+$/.test(value) || value == null;

            if (!isValid) {
                throw this.createError({ message: 'Invalid data. \'Phone Number\' must be a valid number' });
            }

            return true;
        }),


    contactEmail: Yup.string("Email")
        .transform((originalValue) => {
            // Realizar transformaciones si es necesario
            return typeof originalValue === 'string' ? originalValue.trim() : originalValue;
        })
        .nullable(true)
        .test('is-email', 'Invalid data. \'Email\' must be valid', function (value) {
            // Si el valor es null o undefined, lo consideramos válido y no aplicamos más validaciones
            if (value == null || value == undefined) {
                return true;
            }

            const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;

            // Verificar si es una cadena antes de intentar dividirla
            if (typeof value === 'string') {
                const emails = value.split(/[,;:]+/);  // Separar por comas, punto y coma, o dos puntos

                for (const email of emails) {
                    if (!emailRegex.test(email.trim())) {
                        throw this.createError({ message: `Invalid data. '${email}' must be a valid email address` });
                    }
                }
            }

            return true;
        }),
    countryCode: Yup.string("Country Code").nullable(true),
    tentTCategory: Yup.string("tent category is alphanumeric").nullable(true),
    nodeNameval: Yup.string("End Node Name is alphanumeric").nullable(true),
    dateValidTo: Yup.date("Valid from is a Date").nullable(true),
    dateValidFrom: Yup.date("Valid to is a Date").nullable(true),
    dateValidFor: Yup.string("Valid for is a Year").nullable(true),
    remarks: Yup.string("remarks").nullable(true),

    airportsLivePmDescDTO: Yup.object().shape({
        projectName: Yup.string('Value should be a text').nullable(true),
        typeForeseenInterven: Yup.string('Value should be a text').nullable(true),
        length: Yup.string('Value should be a text').nullable(true),
        totalCostCapex: createNumberValidation('Total Cost (CAPEX) (Euro)'),
        estiImpDeadline: Yup.string().nullable(true)
            .test('dateAndStatusValidation', 'Either change the implementation date or set the status to "Implemented"', function (value) {
                const yearValidFor = this.options.context.dateValidFor;
                const implementedStatus = this.options.context.airportsLivePmStatusDTO.implemented;

                /* console.log('yearValidFor:', yearValidFor);
                console.log('implementedStatus:', implementedStatus);
                console.log('value:', value);
 */
                // Verificar si la fecha de implementación tiene el mismo año que la fecha válida y el estado no es "Implemented"
                if (value && value.slice(0, 4) == yearValidFor && implementedStatus != 'Implemented') {
                    //console.log('Failed validation');
                    return false; // La validación falla
                }

                //console.log('Passed validation');
                return true; // La validación pasa
            }),

        projIncreaseCapacity: Yup.boolean().nullable(true),
        capacityBefProj: createNumberValidation('Capacity before project implementation (no./year)'),
        capacityAftProj: createNumberValidation('Capacity after project implementation (no./year)'),
        remarks: Yup.string("remarks").nullable(true)
    }),
    airportsLivePmEligiTentDTO: Yup.object().shape({
        railConn: Yup.boolean().nullable(true),
        cleanFuelsAvail: Yup.string('Value should be a text').nullable(true),
        terminalAvail: Yup.boolean().nullable(true),
        remarks: Yup.string("remarks").nullable(true),
        preConditionedAirSupply: Yup.boolean().nullable(true),
        alternativeFuelsAvailability: Yup.boolean().nullable(true)
    }),
    airportsLivePmIndicatorsDTO: Yup.object().shape({
        siAnnTraffDemGro: createNumberValidation('Annual traffic demand growth (%)'),
        siModalTrans: createNumberValidation('Modal transfer - if applicable (%)'),
        siAnnTraffAcciRateRed: createNumberValidation('Annual accident rate reduction - if applicable (%)'),
        eiEirr: createNumberValidation('Economic internal rate of return (%)'),
        eiNpv: createNumberValidation('Net present value (Euro)'),
        eiSdr: createNumberValidation('Social discount rate (%)'),
        eiProjPlanDesCost: createNumberValidation('Project planning & design cost (Euro)'),
        eiProjConstCost: createNumberValidation('Project construction cost (Euro)'),
        eiTotalProjCost: createNumberValidation('Total project cost (Euro)'),
        fiFirr: createNumberValidation('Financial internal rate of return (%)'),
        fiFnpv: createNumberValidation('Financial net present value (Euro)'),
        fiFdr: createNumberValidation('Financial discount rate (%)'),
        fiWacc: createNumberValidation('Weighted average cost of capital (%)'),
        fiFirtsYearProf: Yup.date("Valid from is a Date").nullable(true),
        fiDscr: createNumberValidation('Debt service coverage ratio (%)'),
        eniCo2Ems: createNumberValidation('CO2 emissions (+/- %)'),
        eniNoxEms: createNumberValidation('Nox emissions (+/- %)'),
        eniSo2Ems: createNumberValidation('SO2 emissions (+/- %)'),
        eniNmhcEms: createNumberValidation('Non-methane hydrocarbons (+/- %)'),
        eniPpm: createNumberValidation('Particulate matter – ppm (+/- %)'),
        /* eniNoiseLevel: createNumberValidation('TODIS'), */
        eniCliChangeRes: Yup.string("remarks").nullable(true),
        eniProteNatuaAff: createNumberValidation('Protected natural areas affected (km2)'),
        remarks: Yup.string("remarks").nullable(true)
    }),
    airportsLivePmPiDTO: Yup.object().shape({
        forecastConstPeriod: Yup.number('Value should be number').nullable(true),
        actualConstPeriod: Yup.number('Value should be number').nullable(true),
        forecastCapex: Yup.number('Value should be number').nullable(true),
        actualCapex: Yup.number('Value should be number').nullable(true),
        forecastOpex: Yup.number('Value should be number').nullable(true),
        actualOpex: Yup.number('Value should be number').nullable(true),
        forecastMaintCost: Yup.number('Value should be number').nullable(true),
        actualMaintCost: Yup.number('Value should be number').nullable(true),
        interestDuringConst: Yup.number('Value should be number').nullable(true),
        ebitda: Yup.number('Value should be number').nullable(true),
        forecastRevenue: Yup.number('Value should be number').nullable(true),
        actualRevenue: Yup.number('Value should be number').nullable(true),
        forecastThroughputTraffic: Yup.number('Value should be number').nullable(true),
        actualThroughputTraffic: Yup.number('Value should be number').nullable(true),
        forecastPassTraffic: Yup.number('Value should be number').nullable(true),
        actualPassTraffic: Yup.number('Value should be number').nullable(true),
        forecastFreightTn: Yup.number('Value should be number').nullable(true),
        actualFreightTn: Yup.number('Value should be number').nullable(true),
        remarks: Yup.string("remarks").nullable(true)
    }),
    airportsLivePmStatusDTO: Yup.object().shape({
        implemented: Yup.string('Value should be a text').nullable(true),
        ongoingProjFs: Yup.string('Value should be a text').nullable(true),
        matureProjFs: Yup.string('Value should be a text').nullable(true),
        projUnderPrep: Yup.string('Value should be a text').nullable(true),
        projStatusDesc: Yup.string('Value should be a text').nullable(true),
        initProjCompDate: Yup.date("Valid to is a Date").nullable(true),
        actualProjCompDate: Yup.date("Valid to is a Date").nullable(true),
        projFundNatBudg: createNumberValidation('National Budget'),
        projFundWb: createNumberValidation('WB'),
        projFundEbrd: createNumberValidation('EBRD'),
        projFundEib: createNumberValidation('EIB'),
        otherIfi: createNumberValidation('IFI'),
        otherIfiSpec: Yup.string('Value should be a text').nullable(true),
        concessions: createNumberValidation('Concessions'),
        concessionSpec: Yup.string('Value should be a text').nullable(true),
        euFund: createNumberValidation('EU Fund'),
        euFundSpec: Yup.string('Value should be a text').nullable(true),
        otherFund: Yup.string('Value should be a text').nullable(true),
        otherFundSpec: Yup.string('Value should be a text').nullable(true),
        projDocTitle: Yup.string('Value should be a text').nullable(true),
        projDocPrepBy: Yup.string('Value should be a text').nullable(true),
        projDocSupervBy: Yup.string('Value should be a text').nullable(true),
        remarks: Yup.string("remarks").nullable(true)
    }),
    airportsLivePmTentcompDTO: Yup.object().shape({
        befRailConn: Yup.boolean().nullable(true),
        aftRailConn: Yup.boolean().nullable(true),
        befCleanFuels: Yup.string('Value should be a text').nullable(true),
        aftCleanFuels: Yup.string('Value should be a text').nullable(true),
        befTerminalAvail: Yup.boolean().nullable(true),
        aftTerminalAvail: Yup.boolean().nullable(true),
        remarks: Yup.string("remarks").nullable(true),
        befPreConditionedAirSupply: Yup.string("remarks").nullable(true),
        aftPreConditionedAirSupply: Yup.string("remarks").nullable(true),
        befAlternativeFuelsAvailability: Yup.boolean().nullable(true),
        aftAlternativeFuelsAvailability: Yup.boolean().nullable(true),
    }),
});

const LAIRPORT_INIT = {
    //general section Rules:
    nameResp: "",
    addressResp: "",
    contactPer: "",
    contactPos: "",
    conctactNum: 0,
    contactEmail: "",
    countryCode: "",
    tentTCategory: "",
    nodeNameval: "",
    dateValidTo: "",
    dateValidFrom: "",
    dateValidFor: "",
    remarks: "",
    airportsLivePmDescDTO: {
        projectName: "",
        typeForeseenInterven: "",
        length: "",
        totalCostCapex: "",
        estiImpDeadline: "",
        projIncreaseCapacity: false,
        capacityBefProj: "",
        capacityAftProj: "",
        remarks: "",
    },
    airportsLivePmEligiTentDTO: {
        railConn: false,
        cleanFuelsAvail: false,
        terminalAvail: false,
        remarks: "",
        preConditionedAirSupply: false,
        alternativeFuelsAvailability: false,
    },
    airportsLivePmIndicatorsDTO: {
        siAnnTraffDemGro: "",
        siModalTrans: "",
        siAnnTraffAcciRateRed: "",
        eiEirr: "",
        eiNpv: "",
        eiSdr: "",
        eiProjPlanDesCost: "",
        eiProjConstCost: "",
        eiTotalProjCost: "",
        fiFirr: "",
        fiFnpv: "",
        fiFdr: "",
        fiWacc: "",
        fiFirtsYearProf: "",
        fiDscr: "",
        eniCo2Ems: "",
        eniNoxEms: "",
        eniSo2Ems: "",
        eniNmhcEms: "",
        eniPpm: "",
        eniNoiseLevel: "",
        eniCliChangeRes: "",
        eniProteNatuaAff: "",
        remarks: ""
    },
    airportsLivePmPiDTO: {
        forecastConstPeriod: "",
        actualConstPeriod: "",
        forecastCapex: "",
        actualCapex: "",
        forecastOpex: "",
        actualOpex: "",
        forecastMaintCost: "",
        actualMaintCost: "",
        interestDuringConst: "",
        ebitda: "",
        forecastRevenue: "",
        actualRevenue: "",
        forecastThroughputTraffic: "",
        actualThroughputTraffic: "",
        forecastPassTraffic: "",
        actualPassTraffic: "",
        forecastFreightTn: "",
        actualFreightTn: "",
        remarks: ""
    },
    airportsLivePmStatusDTO: {
        implemented: "",
        ongoingProjFs: "",
        matureProjFs: "",
        projUnderPrep: "",
        projStatusDesc: "",
        initProjCompDate: "",
        actualProjCompDate: "",
        projFundNatBudg: "",
        projFundWb: "",
        projFundEbrd: "",
        projFundEib: "",
        otherIfi: "",
        otherIfiSpec: "",
        concessions: "",
        concessionSpec: "",
        euFund: "",
        euFundSpec: "",
        otherFund: "",
        otherFundSpec: "",
        projDocTitle: "",
        projDocPrepBy: "",
        projDocSupervBy: "",
        remarks: ""
    },
    airportsLivePmTentcompDTO: {
        befRailConn: false,
        aftRailConn: false,
        befCleanFuels: false,
        aftCleanFuels: false,
        befTerminalAvail: false,
        aftTerminalAvail: false,
        remarks: "",
        befPreConditionedAirSupply: false,
        aftPreConditionedAirSupply: false,
        befAlternativeFuelsAvailability: false,
        aftAlternativeFuelsAvailability: false,
    },
}

export { TODIS_LAIRPORT_SCHEMA, LAIRPORT_INIT };